import { Input, InputProps } from "@chakra-ui/react";
import React from "react";
import {
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import { FormFieldControl } from "./FormFieldControl";

type InputFieldProps<TFormValues extends FieldValues> = Omit<
  InputProps,
  "name" | "form" | "label"
> & {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  label?: React.ReactNode;
  rules?: RegisterOptions<TFormValues, FieldPath<TFormValues>>;
  helperText?: React.ReactNode;
};

/**
 * A simple text input field for react-hook-form.
 *
 * It wraps FormFieldControl and automatically registers the Chakra Input
 * with react-hook-form, including the required rule when `isRequired` is true.
 *
 * Use FormFieldControl directly when you need custom input composition,
 * such as InputGroup addons, custom controls, or multiple inputs.
 */
export function InputField<T extends FieldValues>({
  form,
  name,
  label,
  rules,
  helperText,
  isRequired,
  isDisabled,
  ...inputProps
}: InputFieldProps<T>) {
  const requiredRule =
    rules?.required ??
    (isRequired ? `${label ?? "This field"} is required` : false);

  return (
    <FormFieldControl
      form={form}
      name={name}
      label={label}
      helperText={helperText}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Input
        {...inputProps}
        {...form.register(name, {
          ...rules,
          required: requiredRule,
        })}
      />
    </FormFieldControl>
  );
}
